import React, { useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import "dayjs/locale/pl";
import axios from "../../axiosConfig";
import { TrainerContext } from "../../context/TrainerContext";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { IoSearchOutline } from "react-icons/io5";
import "../../styles/TrainerCanceledTrainings.css";

dayjs.locale("pl");

/**
 * Component for displaying canceled training sessions
 * Allows filtering by trainer or viewing all trainers in the school
 */
const TrainerCanceledTrainings = ({ data }) => {
    const { t } = useTranslation('trainings');
    const { trainer } = useContext(TrainerContext);

    // State management
    const [trainersList, setTrainersList] = useState([]);
    const [selectedTrainerId, setSelectedTrainerId] = useState(trainer?.id || null);
    const [searchQuery, setSearchQuery] = useState("");
    const [expandedTrainingId, setExpandedTrainingId] = useState(null);
    const [canceledTrainingsData, setCanceledTrainingsData] = useState(data || []);
    const [isLoadingTrainings, setIsLoadingTrainings] = useState(false);

    /**
     * Fetch all trainers associated with the school
     */
    useEffect(() => {
        if (!trainer?.schoolId) return;

        const token = localStorage.getItem("jwt");

        axios
            .get(`/api/v1/trainer/find-all/${trainer.schoolId}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((response) => {
                const trainers = response.data || [];
                setTrainersList(trainers);

                if (!selectedTrainerId && trainers.length > 0) {
                    setSelectedTrainerId(trainer.id);
                }
            })
            .catch((error) => {
                console.error("Error fetching trainers:", error);
            });
    }, [trainer, selectedTrainerId]);

    /**
     * Fetch canceled trainings for the selected trainer or school
     */
    useEffect(() => {
        if (!selectedTrainerId || !trainer?.schoolId) return;

        const token = localStorage.getItem("jwt");
        const timeFrom = dayjs()
            .subtract(1, "month")
            .startOf("month")
            .format("YYYY-MM-DDTHH:mm:ss");
        const timeTo = dayjs()
            .add(1, "month")
            .endOf("month")
            .format("YYYY-MM-DDTHH:mm:ss");

        setIsLoadingTrainings(true);

        let endpoint;
        if (selectedTrainerId === 'school') {
            endpoint = `/api/v1/archive/canceled-training/${trainer.schoolId}/school?timeFrom=${timeFrom}&timeTo=${timeTo}`;
        } else {
            endpoint = `/api/v1/archive/canceled-training/${selectedTrainerId}/trainer?timeFrom=${timeFrom}&timeTo=${timeTo}`;
        }

        axios
            .get(endpoint, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((response) => {
                setCanceledTrainingsData(response.data || []);
            })
            .catch((error) => {
                console.error("Error fetching canceled trainings:", error);
                setCanceledTrainingsData([]);
            })
            .finally(() => {
                setIsLoadingTrainings(false);
            });
    }, [selectedTrainerId, trainer?.schoolId]);

    /**
     * Handle trainer tab selection
     * @param {string} trainerId - Selected trainer's ID or 'school' for all trainers
     */
    const handleTrainerSelection = (trainerId) => {
        setSelectedTrainerId(trainerId);
        setExpandedTrainingId(null);
    };

    /**
     * Format date and time for display
     * @param {string} dateTime - ISO date string
     * @returns {string} Formatted date and time
     */
    const formatDateTime = (dateTime) => {
        return dayjs(dateTime).format("DD.MM.YYYY HH:mm");
    };

    /**
     * Toggle training details expansion
     * @param {string} trainingId - Training ID to toggle
     */
    const toggleTrainingDetails = (trainingId) => {
        setExpandedTrainingId(
            expandedTrainingId === trainingId ? null : trainingId
        );
    };

    const canceledTrainings = Array.isArray(canceledTrainingsData) ? canceledTrainingsData : [];

    // Sort trainings by cancellation time (most recent first)
    const sortedTrainings = [...canceledTrainings].sort(
        (a, b) => dayjs(b.cancellationTime).valueOf() - dayjs(a.cancellationTime).valueOf()
    );

    /**
     * Filter trainings based on search query
     */
    const filteredTrainings = sortedTrainings.filter((training) => {
        const searchTerm = searchQuery.toLowerCase();

        const dogName = training.dogDto?.name?.toLowerCase() || "";
        const dogBreed = training.dogDto?.breed?.toLowerCase() || "";
        const ownerName = training.dogDto?.ownerName?.toLowerCase() || "";
        const trainerName = training.trainerName?.toLowerCase() || "";
        const trainingDate = formatDateTime(training.dateTime).toLowerCase();
        const cancellationDate = formatDateTime(training.cancellationTime).toLowerCase();

        return (
            dogName.includes(searchTerm) ||
            dogBreed.includes(searchTerm) ||
            ownerName.includes(searchTerm) ||
            trainerName.includes(searchTerm) ||
            trainingDate.includes(searchTerm) ||
            cancellationDate.includes(searchTerm)
        );
    });

    return (
        <div className="canceled-trainings-wrapper">
            {/* Header with search bar */}
            <div className="canceled-trainings-header">
                <div></div>
                <div className="search-bar-container">
                    <IoSearchOutline className="search-bar-icon" />
                    <input
                        type="text"
                        placeholder={t('searchPlaceholder')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="search-bar-input"
                    />
                </div>
            </div>

            {/* Trainer tabs with school-wide option */}
            <div className="trainer-tabs-container">
                <div className="trainer-tabs-list">
                    {trainersList.map((trainerItem) => (
                        <button
                            key={trainerItem.id}
                            onClick={() => handleTrainerSelection(trainerItem.id)}
                            className={
                                trainerItem.id === selectedTrainerId
                                    ? "trainer-tab-button trainer-tab-button--active"
                                    : "trainer-tab-button"
                            }
                        >
                            {trainerItem.firstName}
                        </button>
                    ))}
                    <button
                        onClick={() => handleTrainerSelection('school')}
                        className={
                            selectedTrainerId === 'school'
                                ? "trainer-tab-button trainer-tab-button--active"
                                : "trainer-tab-button"
                        }
                    >
                        {t('wholeSchool')}
                    </button>
                </div>
            </div>

            {/* Canceled trainings list */}
            <div className="trainings-list-container">
                {isLoadingTrainings ? (
                    <p className="no-trainings-message">{t('loadingCanceledTrainings')}</p>
                ) : filteredTrainings.length === 0 ? (
                    <p className="no-trainings-message">
                        {t('noCanceledTrainings')}
                    </p>
                ) : (
                    filteredTrainings.map((training, index) => {
                        const trainingId = `canceled-${index}`;

                        return (
                            <div
                                key={trainingId}
                                className="training-card training-card--canceled"
                            >
                                {/* Training card header */}
                                <div className="training-card-header">
                                    <div
                                        className="training-card-summary"
                                        onClick={() => toggleTrainingDetails(trainingId)}
                                    >
                                        <span className="training-card-name">
                                            {training.dogDto?.name}
                                        </span>
                                        <span className="training-card-date">
                                            {formatDateTime(training.dateTime)}
                                        </span>
                                        <span className="training-card-cancellation-status">
                                            {t('canceledStatus')}
                                            {training.canceledByOwner
                                                ? ` (${t('byOwner')})`
                                                : ` (${t('byTrainer')})`}
                                        </span>
                                        {selectedTrainerId === 'school' && (
                                            <span className="training-card-trainer-name">
                                                {training.trainerName}
                                            </span>
                                        )}
                                    </div>

                                    <div className="training-card-actions">
                                        <div
                                            className="training-expand-button"
                                            onClick={() => toggleTrainingDetails(trainingId)}
                                        >
                                            {expandedTrainingId === trainingId ? (
                                                <FaChevronUp className="training-expand-icon" />
                                            ) : (
                                                <FaChevronDown className="training-expand-icon" />
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Expanded training details */}
                                {expandedTrainingId === trainingId && (
                                    <div className="training-card-details">
                                        <div className="training-details-content">
                                            <p>
                                                <strong>{t('dog')}:</strong> {training.dogDto?.name}
                                            </p>
                                            <p>
                                                <strong>{t('breed')}:</strong> {training.dogDto?.breed}
                                            </p>
                                            <p>
                                                <strong>{t('ownerInfo')}:</strong>{" "}
                                                {training.dogDto?.ownersName}
                                            </p>
                                            {selectedTrainerId === 'school' && (
                                                <p>
                                                    <strong>{t('trainer')}:</strong> {training.trainerName}
                                                </p>
                                            )}
                                            <p>
                                                <strong>{t('trainingDate')}:</strong>{" "}
                                                {formatDateTime(training.dateTime)}
                                            </p>
                                            <p>
                                                <strong>{t('cancellationDate')}:</strong>{" "}
                                                {formatDateTime(training.cancellationTime)}
                                            </p>
                                            <p>
                                                <strong>{t('canceledBy')}:</strong>{" "}
                                                {training.canceledByOwner
                                                    ? t('ownerAccusative')
                                                    : t('trainerAccusative')}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default TrainerCanceledTrainings;